/**
 * iter841 — Denomination-counter input for the End-Of-Day cash count.
 *
 * Small grid of number inputs for each SAR/currency denomination.
 * As the cashier types counts, we auto-compute the total and bubble
 * it up via onChange. Denominations reflect Saudi Riyal notes/coins
 * by default but the list is prop-configurable for other tenants.
 */
import { Input } from '@/components/ui/input';

// SAR notes + common coins. Cashiers count high-to-low which reduces
// mistakes; we render in that order.
export const SAR_DENOMS = [500, 200, 100, 50, 20, 10, 5, 1, 0.5, 0.25];

export default function DenominationCounter({
  denoms = SAR_DENOMS,
  values,
  onChange,
  currencySymbol = 'SR',
}) {
  const total = denoms.reduce((s, d) => s + d * (Number(values?.[d]) || 0), 0);
  const update = (d, v) => {
    const clean = v.replace(/[^0-9]/g, '');
    const next = { ...(values || {}), [d]: clean === '' ? '' : Number(clean) };
    onChange?.(next, denoms.reduce((s, dd) => s + dd * (Number(next[dd]) || 0), 0));
  };
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
        Count by denomination
      </div>
      <div className="grid grid-cols-2 gap-2" data-testid="denom-counter">
        {denoms.map((d) => (
          <label key={d} className="flex items-center gap-2">
            <span className="w-14 text-right text-xs font-semibold tabular-nums text-muted-foreground">
              {currencySymbol} {d}
            </span>
            <span className="text-muted-foreground text-xs">×</span>
            <Input
              type="text"
              inputMode="numeric"
              className="h-9 flex-1 text-sm text-center tabular-nums"
              value={values?.[d] ?? ''}
              onChange={(e) => update(d, e.target.value)}
              placeholder="0"
              data-testid={`denom-${d}`}
            />
            <span className="w-16 text-right text-[11px] font-medium tabular-nums text-foreground">
              {d * (Number(values?.[d]) || 0) > 0 ? (d * (Number(values?.[d]) || 0)).toFixed(2) : '—'}
            </span>
          </label>
        ))}
      </div>
      <div className="mt-2 pt-2 border-t border-border/60 flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground">Total counted</span>
        <span className="text-base font-bold tabular-nums text-foreground" data-testid="denom-total">
          {currencySymbol} {total.toFixed(2)}
        </span>
      </div>
    </div>
  );
}
